/**
 * Return Order Summary Component
 *
 * Displays the summary information for a return order including
 * order details and people/register information.
 */

import { Avatar, Box, Card, CardContent, Chip, Grid2, Typography } from "@mui/material"
import PersonIcon from "@mui/icons-material/Person"
import StorefrontIcon from "@mui/icons-material/Storefront"
import Stack from "src/shared/components/stack"
import { getDisplayPrice } from "src/shared/utils/helpers-functions"
import { getStatusColor, getStatusIcon } from "../utils"
import KeyValue from "./KeyValue"
import moment from "moment"

type ReturnOrderSummaryProps = {
  detail: any
}

/**
 * Summary component for return order details
 */
export default function ReturnOrderSummary({ detail }: ReturnOrderSummaryProps) {
  return (
    <Grid2 container spacing={2}>
      {/* Order Summary Card */}
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Card variant="outlined" sx={{ height: "100%" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Order Summary
            </Typography>

            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12 }}>
                <KeyValue label="Return ID" value={detail.secondaryId ?? ""} />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <KeyValue label="Parent Order" value={detail.order?.secondaryId ?? ""} />
              </Grid2>
              <Grid2 size={{ xs: 6 }}>
                <KeyValue label="Created" value={moment(detail.createdAt).format("lll")} />
              </Grid2>
              <Grid2 size={{ xs: 6 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Status
                  </Typography>
                  <Chip
                    size="small"
                    color={getStatusColor(detail.status)}
                    label={detail.status.replace("_", " ")}
                    icon={getStatusIcon(detail.status)}
                    sx={{ fontWeight: 500 }}
                  />
                </Box>
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Total amount
                </Typography>
                <Typography variant="h6" fontWeight={600} color="primary">
                  {getDisplayPrice(detail.totalAmount)}
                </Typography>
              </Grid2>
            </Grid2>
          </CardContent>
        </Card>
      </Grid2>

      {/* People & Register Card */}
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Card variant="outlined" sx={{ height: "100%" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              People & Register
            </Typography>

            <Stack gap={2}>
              {/* Customer */}
              <Stack variant="rowCenter" gap={2}>
                <Avatar sx={{ width: 40, height: 40, bgcolor: "primary.main" }}>
                  <PersonIcon />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Customer
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {detail.customer?.name ?? "N/A"}
                  </Typography>
                </Box>
              </Stack>

              {/* Processed By */}
              <Stack variant="rowCenter" gap={2}>
                <Avatar sx={{ width: 40, height: 40, bgcolor: "secondary.main" }}>
                  <PersonIcon />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Processed By
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {`${detail.locationRegisterSession?.openedBy?.firstName} ${detail.locationRegisterSession?.openedBy?.lastName}`}
                  </Typography>
                </Box>
              </Stack>

              {/* Register */}
              <Stack variant="rowCenter" gap={2}>
                <Avatar sx={{ width: 40, height: 40, bgcolor: "success.main" }}>
                  <StorefrontIcon />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Register
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {detail.location?.name ?? "N/A"}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Grid2>
    </Grid2>
  )
}
